export type WorkspaceBillingAlertData = {
  subscription_status?: string | null;
  subscription_mismatch?:
    | "local_subscription_missing"
    | "stripe_subscription_canceled"
    | "stripe_subscription_missing"
    | null;
  actors?: Array<{
    period_start: string;
    status: string;
  }>;
  usage?: {
    subscription: {
      pricing_model: "fixed" | "per_seat";
      seats: number;
      status: string;
      payment_grace_period_end: string | null;
    } | null;
  };
};

export type WorkspaceBillingAlert = {
  tone: "warning" | "danger";
  message: string;
  action?: string;
};

const paymentFailureStatuses = new Set([
  "past_due",
  "unpaid",
  "incomplete_expired",
  "paused",
]);

function formatDeadline(value: Date) {
  return new Intl.DateTimeFormat(undefined, {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(value);
}

export function getWorkspaceBillingAlert(
  billing: WorkspaceBillingAlertData | undefined,
  now = new Date(),
): WorkspaceBillingAlert | null {
  const subscription = billing?.usage?.subscription;
  if (!billing) {
    return null;
  }

  if (billing.subscription_status === "canceled") {
    return {
      tone: "warning",
      message: subscription
        ? "Your paid Stripe subscription was canceled. The workspace is now on the Free plan. Choose a plan to subscribe again."
        : "Your paid Stripe subscription was canceled. Choose a plan to subscribe again and restore Terrabuild access.",
      action: "Choose a plan",
    };
  }

  if (!subscription) {
    return null;
  }

  if (billing.subscription_mismatch) {
    const detail =
      billing.subscription_mismatch === "stripe_subscription_canceled"
        ? "Stripe reports that the subscription is canceled"
        : billing.subscription_mismatch === "stripe_subscription_missing"
          ? "The Stripe subscription no longer exists"
          : "Insights has no Stripe subscription for this paid plan";
    return {
      tone: "danger",
      message: `Subscription mismatch: ${detail}, but Insights still records a paid plan. Plan changes are disabled. Contact support.`,
    };
  }

  const subscriptionStatus =
    billing.subscription_status ?? subscription.status;
  if (paymentFailureStatuses.has(subscriptionStatus)) {
    const graceEnd = subscription.payment_grace_period_end
      ? new Date(subscription.payment_grace_period_end)
      : null;
    if (graceEnd && !Number.isNaN(graceEnd.getTime()) && now <= graceEnd) {
      return {
        tone: "warning",
        message: `Payment failed. Update the payment method before ${formatDeadline(graceEnd)} to avoid suspension.`,
        action: "Review billing",
      };
    }

    return {
      tone: "danger",
      message:
        "Terrabuild access is suspended because of a payment failure. Update the payment method to restore access.",
      action: "Review billing",
    };
  }

  if (subscription.pricing_model !== "per_seat") {
    return null;
  }

  const periodPrefix = `${now.getUTCFullYear()}-${String(
    now.getUTCMonth() + 1,
  ).padStart(2, "0")}`;
  const actors = (billing.actors ?? []).filter((actor) =>
    actor.period_start.startsWith(periodPrefix),
  );
  const rejectedActors = actors.filter(
    (actor) => actor.status === "rejected",
  );
  if (rejectedActors.length === 0) {
    return null;
  }

  const userLabel = actors.length === 1 ? "user has" : "users have";
  const seatLabel = subscription.seats === 1 ? "seat" : "seats";
  return {
    tone: "warning",
    message: `${actors.length} active ${userLabel} submitted builds this month, but your plan includes ${subscription.seats} ${seatLabel}. Extra users are blocked.`,
    action: "Change plan",
  };
}
